import React from 'react';
import {
  Box, Typography, Stack, Button, Chip, CircularProgress, Divider, Tooltip,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BlockIcon from '@mui/icons-material/Block';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CoordinatePatient, InsuranceTrackStatus } from '~/types/coordinate';
import { useCoordinateContext } from '~/hooks/coordinate/useCoordinateContext';

// ── Commercial / Medicare Advantage / Medicaid auth panel ──────────────────

interface StatusConfig {
  label: string;
  color: string;
  bgcolor: string;
  icon: React.ReactNode;
}

const STATUS_CONFIG: Record<InsuranceTrackStatus, StatusConfig> = {
  authorized:   { label: 'Authorized',           color: '#006e60', bgcolor: '#e0f7f4', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
  not_required: { label: 'Not required',          color: '#006e60', bgcolor: '#e0f7f4', icon: <BlockIcon sx={{ fontSize: 16 }} /> },
  checking:     { label: 'Checking…',             color: '#153873', bgcolor: '#e5f6fb', icon: <CircularProgress size={13} sx={{ color: '#153873' }} /> },
  pending:      { label: 'Pending response',      color: '#153873', bgcolor: '#e5f6fb', icon: <HelpOutlineIcon sx={{ fontSize: 16 }} /> },
  issue:        { label: 'Issue — action needed', color: '#e65100', bgcolor: '#fff3e0', icon: <WarningAmberIcon sx={{ fontSize: 16 }} /> },
  not_checked:  { label: 'Not checked',           color: '#668098', bgcolor: '#f6f6f6', icon: <HelpOutlineIcon sx={{ fontSize: 16 }} /> },
};

const SUMMARY_TEXT: Record<InsuranceTrackStatus, string> = {
  authorized:   'Prior authorization confirmed. This patient is cleared for post-acute services.',
  not_required: 'No prior authorization is required for this insurance plan.',
  checking:     'Coordinate AI is verifying authorization with the payer. This usually takes a few seconds.',
  pending:      'Prior auth request has been submitted. Awaiting payer response — typically 24–72 hours.',
  issue:        'An authorization issue was detected. Review the notes below and retry, or contact the payer directly.',
  not_checked:  'Prior authorization has not been verified yet. Check authorization before sending the referral.',
};

const AuthPanel: React.FC<{ patient: CoordinatePatient }> = ({ patient }) => {
  const { checkInsuranceAuth } = useCoordinateContext();
  const ins = patient.insuranceTrack;
  const status = ins?.status ?? 'not_checked';
  const config = STATUS_CONFIG[status];
  const canCheck = ['not_checked', 'pending', 'issue'].includes(status);
  const isChecking = status === 'checking';

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{patient.insurance}</Typography>
          {patient.memberId && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Member ID: {patient.memberId}
            </Typography>
          )}
        </Box>
        <Chip
          label={config.label}
          size="small"
          icon={<Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>{config.icon}</Box>}
          sx={{
            fontWeight: 700, fontSize: '0.78rem',
            bgcolor: config.bgcolor, color: config.color,
            '& .MuiChip-icon': { color: config.color },
          }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, fontSize: '0.68rem' }}>
        Authorization Status
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75, mb: 2.5, lineHeight: 1.6 }}>
        {SUMMARY_TEXT[status]}
      </Typography>

      {ins?.authNumber && (
        <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ minWidth: 120, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Auth Number
          </Typography>
          <Typography variant="body2" fontWeight={600}>{ins.authNumber}</Typography>
        </Stack>
      )}

      {ins?.checkedAt && (
        <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ minWidth: 120, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Last Checked
          </Typography>
          <Typography variant="body2">
            {new Date(ins.checkedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Stack>
      )}

      {ins?.notes && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.75 }}>
            Notes
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.7, p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}
          >
            {ins.notes}
          </Typography>
        </Box>
      )}

      {canCheck && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={isChecking
              ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
              : <VerifiedIcon />
            }
            disabled={isChecking}
            onClick={() => checkInsuranceAuth(patient.id)}
            sx={{ fontWeight: 700 }}
          >
            {status === 'issue' ? 'Retry Authorization Check' : 'Check Authorization'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ── Medicare home health documentation panel ──────────────────────────────

const DocCheckRow: React.FC<{
  label: string;
  tooltip: string;
  confirmed: boolean;
  actionLabel?: string;
  onAction?: () => void;
  subText?: string;
}> = ({ label, tooltip, confirmed, actionLabel, onAction, subText }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{ py: 0.875, opacity: confirmed ? 0.5 : 1 }}
  >
    {confirmed
      ? <CheckCircleIcon sx={{ fontSize: 16, color: '#00bda1', flexShrink: 0 }} />
      : <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: '#e65100', flexShrink: 0 }} />
    }
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" fontWeight={confirmed ? 400 : 600} sx={{ fontSize: '0.85rem' }}>
          {label}
        </Typography>
        <Tooltip title={tooltip} placement="top" arrow>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
        </Tooltip>
      </Stack>
      {subText && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
          {subText}
        </Typography>
      )}
    </Box>
    {!confirmed && actionLabel && onAction && (
      <Button
        size="small"
        variant="outlined"
        onClick={onAction}
        sx={{ fontSize: '0.72rem', flexShrink: 0, color: '#006e60', borderColor: '#00bda1', '&:hover': { borderColor: '#006e60', bgcolor: 'rgba(0,189,161,0.04)' } }}
      >
        {actionLabel}
      </Button>
    )}
  </Stack>
);

const MedicarePanel: React.FC<{ patient: CoordinatePatient }> = ({ patient }) => {
  const { updateInsuranceDoc } = useCoordinateContext();
  const ins: Partial<NonNullable<typeof patient.insuranceTrack>> = patient.insuranceTrack ?? {};
  const homebound = ins.homeboundDocumented === true;
  const f2f = ins.f2fEncounterComplete === true;
  const cert485 = ins.physicianCertification485 ?? 'not_started';
  const confirmedCount = [homebound, f2f, cert485 === 'signed'].filter(Boolean).length;
  const allConfirmed = confirmedCount === 3;

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{patient.insurance}</Typography>
          {patient.memberId && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Member ID: {patient.memberId}
            </Typography>
          )}
        </Box>
        <Chip
          label={allConfirmed ? 'Documentation Complete' : 'Documentation Needed'}
          size="small"
          sx={{
            fontWeight: 700, fontSize: '0.78rem',
            bgcolor: allConfirmed ? '#e0f7f4' : '#fff3e0',
            color: allConfirmed ? '#006e60' : '#e65100',
          }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, fontSize: '0.68rem' }}>
          Medicare Home Health Documentation
        </Typography>
        <Typography variant="caption" sx={{ color: allConfirmed ? '#006e60' : 'text.secondary', fontWeight: 600, fontSize: '0.72rem' }}>
          {confirmedCount} of 3 confirmed
        </Typography>
      </Stack>

      <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden', px: 2 }}>
        <DocCheckRow
          label="Homebound Status"
          tooltip="Patient must be substantially confined to the home and leave only with considerable and taxing effort. Document this in the clinical record before discharge."
          confirmed={homebound}
          actionLabel="Mark Confirmed"
          onAction={() => updateInsuranceDoc(patient.id, { homeboundDocumented: true })}
        />
        <Divider />
        <DocCheckRow
          label="Face-to-Face Encounter"
          tooltip="CMS requires a qualifying physician encounter within 90 days before or 30 days after start of care. Verify the encounter note is in the record."
          confirmed={f2f}
          actionLabel="Mark Confirmed"
          onAction={() => updateInsuranceDoc(patient.id, { f2fEncounterComplete: true })}
        />
        <Divider />
        <DocCheckRow
          label="Plan of Care (485)"
          tooltip="The attending physician must sign the Home Health Plan of Care before the agency can begin billing Medicare."
          confirmed={cert485 === 'signed'}
          actionLabel={cert485 === 'not_started' ? 'Mark Sent' : cert485 === 'sent' ? 'Mark Signed' : undefined}
          onAction={cert485 === 'not_started'
            ? () => updateInsuranceDoc(patient.id, { physicianCertification485: 'sent' })
            : cert485 === 'sent'
            ? () => updateInsuranceDoc(patient.id, { physicianCertification485: 'signed' })
            : undefined
          }
          subText={cert485 === 'sent' ? 'Sent — awaiting physician signature' : cert485 === 'not_started' ? 'Not yet sent to physician' : undefined}
        />
      </Box>

      {ins.notes && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.75 }}>
            Notes
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.7, p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200' }}
          >
            {ins.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Router ─────────────────────────────────────────────────────────────────

const InsurancePanel: React.FC<{ patient: CoordinatePatient }> = ({ patient }) => {
  if (patient.insuranceType === 'medicare') {
    return <MedicarePanel patient={patient} />;
  }
  return <AuthPanel patient={patient} />;
};

export default InsurancePanel;
